import { useRef, useEffect, useLayoutEffect } from 'react'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import Box from '@mui/material/Box'
import { SxProps } from '@mui/system'
import { Theme } from '@mui/material/styles'

am4core.useTheme(am4themes_animated)

type BarProps = {
    chartName: string
    chartData: Array<any>
    dataFieldsCategory: string
    titleText?: string
    titleTextSize?: number
    columnColor: string
    responsive?: boolean
    sxProps?: SxProps<Theme>
}
const Bar = (props: BarProps) => {
    const { responsive = false } = props
    let refChart = useRef<am4charts.XYChart>()

    const createChart = () => {
        let chart = am4core.create(props.chartName, am4charts.XYChart)
        refChart.current = chart

        chart.data = props.chartData
        chart.logo.disabled = true
        chart.responsive.enabled = responsive
        chart.maskBullets = false

        if (props.titleText) {
            let title = chart.titles.create()
            title.text = props.titleText
            title.fontSize = props?.titleTextSize ?? 14
            title.marginBottom = 30
            title.fontWeight = 'bold'
            title.wrap = true
        }

        // Create axes
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
        categoryAxis.dataFields.category = props.dataFieldsCategory
        categoryAxis.renderer.grid.template.location = 0
        categoryAxis.renderer.minGridDistance = 30

        // Configure axis label
        let label = categoryAxis.renderer.labels.template
        label.truncate = true
        label.maxWidth = 200
        label.tooltipText = '{category}'

        categoryAxis.events.on('sizechanged', function (ev) {
            let axis = ev.target
            let cellWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex)
            if (cellWidth < axis.renderer.labels.template.maxWidth) {
                axis.renderer.labels.template.rotation = -45
                axis.renderer.labels.template.horizontalCenter = 'right'
                axis.renderer.labels.template.verticalCenter = 'middle'
            } else {
                axis.renderer.labels.template.rotation = 0
                axis.renderer.labels.template.horizontalCenter = 'middle'
                axis.renderer.labels.template.verticalCenter = 'top'
            }
        })

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis())

        const createGrid = (value: number) => {
            let range = valueAxis.axisRanges.create()
            range.value = value
            range.label.text = '{value}'
        }
        valueAxis.renderer.grid.template.disabled = true
        valueAxis.renderer.labels.template.disabled = true
        valueAxis.events.on('ready', function (ev) {
            valueAxis.renderer.minGridDistance = 50 //ev.target.max as number * 10
            ev.target.min = 0
            // ev.target.max = ev.target.max
            createGrid(0)
            createGrid(ev.target.max as number)
        })

        // Create series
        let series = chart.series.push(new am4charts.ColumnSeries())
        series.dataFields.valueY = 'value'
        series.dataFields.categoryX = props.dataFieldsCategory
        series.columns.template.tooltipText = '{value}'
        series.columns.template.fill = am4core.color(props.columnColor)

        let valueLabel = series.bullets.push(new am4charts.LabelBullet())
        valueLabel.label.text = '[bold]{value}'
        valueLabel.label.fontSize = 12
        valueLabel.label.dy = -15
    }

    useLayoutEffect(() => {
        createChart()
        return () => {
            refChart.current?.dispose()
        }
    }, [props.chartData])

    return <Box id={props.chartName} sx={props.sxProps}></Box>
}

export default Bar
